"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

import { api } from "@/lib/api/client";
import type { CostReport } from "@/lib/api/types";
import { BUDGET_STATE_CLASS, BUDGET_STATE_TEXT, barWidth, formatAmount, monthLabel, percent } from "@/lib/cost-report";
import { formatEuro } from "@/lib/format";

/** Kosten des laufenden Monats für die Übersicht (Mockup „API-Kosten“): Budget und Verbrauch je Quelle. */
export function CostSummary({ projectId }: { projectId: number }) {
  const [report, setReport] = useState<CostReport | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api<CostReport>(`/api/costs?project_id=${projectId}`)
      .then((next) => {
        if (!cancelled) setReport(next);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return (
    <section aria-labelledby="cost-summary-title" className="flex max-w-xl flex-col gap-3 rounded-lg border border-line bg-white px-5 py-4">
      <h2 id="cost-summary-title" className="text-base font-semibold">
        API-Kosten {report ? monthLabel(report.month) : ""}
      </h2>
      {failed && <p className="text-[13px] text-muted">Die Kosten konnten nicht geladen werden.</p>}
      {!report && !failed && <p className="text-[13px] text-muted">Kosten werden geladen …</p>}
      {report && (
        <>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-2xl font-semibold tracking-tight">{formatEuro(report.budget.spent_eur)}</span>
            {report.budget.state !== "none" && (
              <span className="text-sm text-muted">
                von {formatEuro(report.budget.monthly_eur)} ({percent(report.budget.share)} %)
              </span>
            )}
            <span className={`rounded px-2 py-0.5 text-xs font-medium ${BUDGET_STATE_CLASS[report.budget.state]}`}>
              {BUDGET_STATE_TEXT[report.budget.state]}
            </span>
          </div>
          {report.budget.state !== "none" && (
            <div
              role="meter"
              aria-label="Verbrauchtes Monatsbudget"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.min(100, percent(report.budget.share))}
              className="h-2 overflow-hidden rounded bg-[#e9ecea]"
            >
              <div className="h-full bg-accent" style={{ width: `${barWidth(report.budget.share)}%` }} />
            </div>
          )}
          {report.sources.length === 0 ? (
            <p className="text-[13px] text-muted">In diesem Monat gab es noch keine Abrufe.</p>
          ) : (
            <ul aria-label="Verbrauch je Quelle" className="flex flex-col">
              {report.sources.map((source) => (
                <li key={source.source} className="flex justify-between gap-3 border-t border-neutral-bg py-1.5 text-[13px]">
                  <span>{source.label}</span>
                  <span className="font-mono">{formatAmount(source.amount, source.unit)}</span>
                </li>
              ))}
            </ul>
          )}
          <Link href="/kosten" className="inline-flex min-h-11 items-center text-[13px] text-accent underline">
            Alle Kosten ansehen
          </Link>
        </>
      )}
    </section>
  );
}
